import { useEffect, useState } from "react";

/**
 * reCAPTCHA Enterprise readiness + execution.
 * The enterprise API attaches `ready`/`execute` asynchronously, so a single
 * check at mount races that init and can leave `isReady` stuck false (submit
 * disabled). We POLL until the API is attached, then register `ready()`.
 */
export function useRecaptcha(siteKey: string) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;

    if (!document.querySelector('script[src*="recaptcha/enterprise.js"]')) {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      document.head.appendChild(script);
    }

    const waitForReady = () => {
      if (cancelled) return;
      const gre = (window as any).grecaptcha?.enterprise;
      if (gre && typeof gre.ready === "function") {
        gre.ready(() => {
          if (!cancelled) setIsReady(true);
        });
      } else {
        setTimeout(waitForReady, 100);
      }
    };
    waitForReady();

    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  const execute = async (action: string): Promise<string | null> => {
    const gre = (window as any).grecaptcha?.enterprise;
    if (!gre || typeof gre.execute !== "function") {
      console.warn("reCAPTCHA not available");
      return null;
    }
    try {
      return await gre.execute(siteKey, { action });
    } catch (error) {
      console.error("❌ reCAPTCHA execution failed:", error);
      return null;
    }
  };

  const reset = () => {
    try {
      const gre = (window as any).grecaptcha?.enterprise;
      const clients = gre?.getClients?.();
      if (clients && Object.keys(clients).length > 0) {
        gre.reset?.(Object.keys(clients)[0]);
      }
    } catch (err) {
      console.error("reCAPTCHA reset error:", err);
    }
  };

  return { isReady, execute, reset };
}
